import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Schedule as ScheduleIcon,
  TrendingUp as ProgressIcon,
  EmojiEvents as TrophyIcon,
  Lightbulb as TipIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const ExamCountdown = ({ studyPlan, onCheckpointClick }) => {
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [hoursRemaining, setHoursRemaining] = useState(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [motivationalMessage, setMotivationalMessage] = useState('');
  const [urgencyLevel, setUrgencyLevel] = useState('normal');

  useEffect(() => {
    if (studyPlan?.endDate) {
      calculateCountdown();
      calculateProgress();
      setMotivationalMessage(getMotivationalMessage());
    }
  }, [studyPlan]);

  const calculateCountdown = () => {
    const examDate = new Date(studyPlan.endDate);
    const now = new Date();
    const diffTime = examDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

    setDaysRemaining(diffDays);
    setHoursRemaining(diffHours);

    // Set urgency level
    if (diffDays <= 7) {
      setUrgencyLevel('critical');
    } else if (diffDays <= 14) {
      setUrgencyLevel('urgent');
    } else if (diffDays <= 30) {
      setUrgencyLevel('moderate');
    } else {
      setUrgencyLevel('normal');
    }
  };

  const calculateProgress = () => {
    if (!studyPlan?.startDate || !studyPlan?.endDate) return;

    const startDate = new Date(studyPlan.startDate);
    const endDate = new Date(studyPlan.endDate);
    const now = new Date();

    const totalDuration = endDate - startDate;
    const elapsed = now - startDate;
    const progress = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100);

    setProgressPercentage(progress);
  };

  const getMotivationalMessage = () => {
    if (daysRemaining <= 0) {
      return "🎯 Exam day is here! You've got this!";
    }

    const messages = {
      critical: [
        "🚨 Final week! Every minute counts now!",
        "⚡ Last push! You're almost there!",
        "🔥 Final stretch! Give it everything you've got!"
      ],
      urgent: [
        "⏰ Two weeks left! Time to intensify your focus!",
        "🎯 Getting close! Stay focused and consistent!",
        "💪 You're in the home stretch! Keep pushing!"
      ],
      moderate: [
        "📚 Good progress! Keep up the momentum!",
        "🌟 You're doing great! Stay on track!",
        "🎯 Steady progress! You're building strong foundations!"
      ],
      normal: [
        "📖 Great start! You have plenty of time to excel!",
        "🌟 Early planning pays off! Keep it up!",
        "🎯 You're ahead of the game! Maintain this pace!"
      ]
    };

    const levelMessages = messages[urgencyLevel] || messages.normal;
    return levelMessages[Math.floor(Math.random() * levelMessages.length)];
  };

  const getUrgencyColor = () => {
    switch (urgencyLevel) {
      case 'critical': return 'from-red-500 to-red-600';
      case 'urgent': return 'from-orange-500 to-orange-600';
      case 'moderate': return 'from-yellow-500 to-yellow-600';
      default: return 'from-blue-500 to-purple-600';
    }
  };

  const getUrgencyIcon = () => {
    switch (urgencyLevel) {
      case 'critical': return '🚨';
      case 'urgent': return '⏰';
      case 'moderate': return '📚';
      default: return '📅';
    }
  };

  const getStudyTip = () => {
    const tips = {
      critical: [
        "Focus on your weakest areas first",
        "Take practice tests to identify gaps",
        "Review key formulas and concepts",
        "Get adequate sleep and stay hydrated"
      ],
      urgent: [
        "Increase study sessions to 2-3 hours daily",
        "Focus on problem-solving and practice",
        "Review previous mock tests",
        "Create summary notes for quick revision"
      ],
      moderate: [
        "Maintain consistent daily study routine",
        "Mix theory with practice problems",
        "Start taking mock tests weekly",
        "Build strong conceptual understanding"
      ],
      normal: [
        "Establish a solid study routine",
        "Focus on understanding fundamentals",
        "Take regular breaks to maintain focus",
        "Start with easier topics to build confidence"
      ]
    };

    const levelTips = tips[urgencyLevel] || tips.normal;
    return levelTips[Math.floor(Math.random() * levelTips.length)];
  };

  if (!studyPlan?.endDate || daysRemaining === null) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      {/* Header */}
      <div className={`bg-gradient-to-r ${getUrgencyColor()} p-6 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              {getUrgencyIcon()} Exam Countdown
            </h3>
            <p className="text-blue-100 mt-1">
              {studyPlan.subjects?.join(', ')} Exam
            </p>
          </div>
          
          <div className="text-right">
            <div className="text-3xl font-bold">
              {daysRemaining > 0 ? daysRemaining : 0}
            </div>
            <div className="text-sm text-blue-100">
              {daysRemaining > 0 ? 'Days Left' : 'Today!'}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Study Progress
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {Math.round(progressPercentage)}%
          </span>
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <motion.div 
            className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Motivational Message */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <TrophyIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Today's Motivation
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {motivationalMessage}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Study Tip */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <TipIcon className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-200 mb-1">
                Study Tip
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                {getStudyTip()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">
          Quick Stats
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {studyPlan.subjects?.length || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Subjects
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {studyPlan.dailyHours || '4h'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Daily Study
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-6">
        <div className="space-y-3">
          <button
            onClick={onCheckpointClick}
            className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <CheckIcon className="w-5 h-5" />
            Daily Checkpoint
          </button>
          
          <button
            onClick={() => window.location.href = '/calendar'}
            className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <ScheduleIcon className="w-5 h-5" />
            View Calendar
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ExamCountdown; 